/**
 * ERDDAP Configuration
 * Tunable parameters to prevent server overload
 */

export const ERDDAP_CONFIG = {
  // Rate limiting
  MIN_REQUEST_INTERVAL_MS: 500, // 500ms between requests for fast retries
  MAX_CONCURRENT_REQUESTS: 1, // Only 1 concurrent request at a time
  
  // Caching
  CACHE_TTL_MS: 10 * 60 * 1000, // 10 minutes cache TTL
  MAX_CACHE_ENTRIES: 30, // Reduced cache size
  
  // Retry configuration
  MAX_RETRIES: 5, // Reasonable retry limit
  INITIAL_RETRY_DELAY_MS: 500, // Start with 500ms
  MAX_RETRY_DELAY_MS: 5000, // Max 5 seconds between retries
  INFINITE_RETRY_FOR_503: false, // Don't retry infinitely
  RETRY_503_INITIAL_DELAY_MS: 1000, // 1 second for first 503 retry
  
  // Request optimization
  PREVIEW_RESOLUTION: 2.0, // Lower resolution for previews
  DATA_RESOLUTION: 0.5, // Higher resolution for actual data
  
  // Batch loading
  BATCH_SIZE: 2, // Load only 2 previews at a time
  BATCH_DELAY_MS: 3000, // 3 seconds between batches
  
  // Timeout
  REQUEST_TIMEOUT_MS: 30000, // 30 second timeout
  
  // Dataset preview specific
  PREVIEW_DAYS_TO_LOAD: 3, // Only load last 3 days for preview
  SKIP_UNAVAILABLE_DATASETS: true, // Skip datasets that fail to load
};

/**
 * Get configuration value with environment override
 */
export function getConfigValue<K extends keyof typeof ERDDAP_CONFIG>(
  key: K
): typeof ERDDAP_CONFIG[K] {
  // Allow environment variables to override
  const envKey = `ERDDAP_${key}`;
  const envValue = process.env[envKey];

  if (envValue !== undefined) {
    const configValue = ERDDAP_CONFIG[key];
    // If the config value is a number, try to parse the env value as a number
    if (typeof configValue === 'number') {
      const numValue = Number(envValue);
      return (isNaN(numValue) ? configValue : numValue) as typeof ERDDAP_CONFIG[K];
    }
    // If the config value is a boolean, parse as boolean
    if (typeof configValue === 'boolean') {
      return (envValue.toLowerCase() === 'true') as typeof ERDDAP_CONFIG[K];
    }
    // Otherwise return the env value as-is
    return envValue as typeof ERDDAP_CONFIG[K];
  }

  return ERDDAP_CONFIG[key];
}